/**
 * Helpers para disparar eventos de conversão do Facebook Pixel (Meta).
 * O código base do pixel é carregado no public/index.html (window.fbq).
 * Estas funções são seguras: se o fbq não estiver disponível, não fazem nada.
 */

type Fbq = (...args: unknown[]) => void;

function getFbq(): Fbq | null {
  if (typeof window === 'undefined') return null;
  const fbq = (window as Window & { fbq?: Fbq }).fbq;
  return typeof fbq === 'function' ? fbq : null;
}

interface CheckoutEventParams {
  value?: number | null;
  currency?: string;
  contentName?: string | null;
}

interface PurchaseEventParams extends CheckoutEventParams {
  /**
   * ID de deduplicação compartilhado com o evento server-side (Conversions API).
   * Avulso: purchase_<chargeId>; assinatura: purchase_sub_<subscriptionId>.
   */
  eventId?: string | null;
}

/** Dispara InitiateCheckout (cliente abriu o checkout de um plano). */
export function trackInitiateCheckout(params: CheckoutEventParams = {}): void {
  const fbq = getFbq();
  if (!fbq) return;

  const { value, currency = 'BRL', contentName } = params;
  const data: Record<string, unknown> = { currency };
  if (typeof value === 'number' && value > 0) data.value = Number(value.toFixed(2));
  if (contentName) data.content_name = contentName;

  fbq('track', 'InitiateCheckout', data);
}

/** Dispara Purchase (pagamento confirmado), com valor e moeda. */
export function trackPurchase(params: PurchaseEventParams = {}): void {
  const fbq = getFbq();
  if (!fbq) return;

  const { value, currency = 'BRL', contentName, eventId } = params;
  const data: Record<string, unknown> = { currency };
  if (typeof value === 'number' && value > 0) data.value = Number(value.toFixed(2));
  if (contentName) data.content_name = contentName;

  if (eventId) {
    fbq('track', 'Purchase', data, { eventID: eventId });
  } else {
    fbq('track', 'Purchase', data);
  }
}
